import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWarehouse } from '../../../../../contexts/WarehouseContext';
import { crossDockService, CrossDockCandidate } from '../../../../../services/crossDockService';
import { POrderLineDto } from '../../../../../types/porders';

/**
 * Crossdock-kandidater i linjelisten (TW 36395306).
 *
 * «Alle linjer» og «Motta valgte» oppretter ankomstlinjer uten at linjen aktiveres, og da kommer
 * crossdock-spørsmålet aldri. Hooken finner varene med restbehov, så linjelisten kan merke dem og
 * holde dem utenfor masse-mottaket på Pro-lager. Operatøren tar dem enkeltvis og får spørsmålet.
 *
 * - Ingen lager med crossdock på → ingen kall, ingen merking, masse-mottak uendret.
 * - Ett kall per lager i listen (normalt ett), uansett antall linjer.
 * - `splitForBulk` venter på svaret i maks CANDIDATES_TIMEOUT_MS. Svarer ikke API-et, tas alle
 *   linjene med som før (samme regel som ved lagring av én linje).
 */

const CANDIDATES_TIMEOUT_MS = 2000;

export interface UseCrossDockCandidatesOptions {
  lines: POrderLineDto[];
  /** Lageret når linjen ikke har eget (ordrens lager) */
  fallbackWarehouseId?: string;
  /** Slår av crossdock uansett lageroppsett (bilmottak/samlemottak er utenfor v1) */
  disabled?: boolean;
}

export interface UseCrossDockCandidatesResult {
  /** Restbehovet for linjens vare, eller undefined når linjen ikke er kandidat */
  candidateFor: (line: POrderLineDto) => CrossDockCandidate | undefined;
  /** Deler linjene i dem som kan masse-mottas og crossdock-linjene som må tas enkeltvis */
  splitForBulk: <T extends POrderLineDto>(lines: T[]) => Promise<{ bulk: T[]; crossDock: T[] }>;
}

export function useCrossDockCandidates({
  lines,
  fallbackWarehouseId,
  disabled = false
}: UseCrossDockCandidatesOptions): UseCrossDockCandidatesResult {
  const { warehouses } = useWarehouse();
  const [candidates, setCandidates] = useState<Map<string, CrossDockCandidate>>(new Map());
  const pendingRef = useRef<Promise<Map<string, CrossDockCandidate>> | null>(null);
  const candidatesRef = useRef<Map<string, CrossDockCandidate> | null>(null);

  const warehouseOf = useCallback(
    (line: POrderLineDto) => line.warehouse || fallbackWarehouseId || '',
    [fallbackWarehouseId]
  );
  const keyOf = (warehouseId: string, itemNo: string) => `${warehouseId}|${itemNo.trim().toLowerCase()}`;

  // Varene per lager, bare for lagre med crossdock på. Serialisert, så kallet ikke gjentas
  // når linjelisten får ny identitet med samme innhold.
  const request = useMemo(() => {
    if (disabled) return '';
    const crossDockWarehouses = new Set(
      warehouses.filter(w => w.grCrossDockingYesNo === true).map(w => w.warehouseID)
    );
    if (crossDockWarehouses.size === 0) return '';

    const byWarehouse: Record<string, string[]> = {};
    lines.forEach(line => {
      const warehouseId = warehouseOf(line);
      if (!line.itemNo || line.itemExistsInMaster === false || !crossDockWarehouses.has(warehouseId)) return;
      const items = byWarehouse[warehouseId] ?? (byWarehouse[warehouseId] = []);
      if (!items.includes(line.itemNo)) items.push(line.itemNo);
    });
    return Object.keys(byWarehouse).length > 0 ? JSON.stringify(byWarehouse) : '';
  }, [disabled, warehouses, lines, warehouseOf]);

  useEffect(() => {
    setCandidates(new Map());
    candidatesRef.current = null;
    pendingRef.current = null;
    if (!request) return;

    const controller = new AbortController();
    const byWarehouse: Record<string, string[]> = JSON.parse(request);

    pendingRef.current = Promise.all(
      Object.entries(byWarehouse).map(([warehouseId, itemNos]) =>
        crossDockService
          .getCandidates(warehouseId, itemNos, controller.signal)
          .then(result => result.map(c => [keyOf(warehouseId, c.itemNo), c] as const))
      )
    )
      .then(results => {
        const map = new Map<string, CrossDockCandidate>(results.flat());
        if (!controller.signal.aborted) {
          candidatesRef.current = map;
          setCandidates(map);
        }
        return map;
      })
      .catch(err => {
        if (!controller.signal.aborted) console.error('Crossdock: kunne ikke hente kandidater', err);
        return new Map<string, CrossDockCandidate>();
      });

    return () => controller.abort();
  }, [request]);

  const candidateFor = useCallback(
    (line: POrderLineDto) => (line.itemNo ? candidates.get(keyOf(warehouseOf(line), line.itemNo)) : undefined),
    [candidates, warehouseOf]
  );

  const splitForBulk = useCallback(async <T extends POrderLineDto>(selected: T[]) => {
    let map = candidatesRef.current;
    if (!map && pendingRef.current) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), CANDIDATES_TIMEOUT_MS); });
      map = await Promise.race([pendingRef.current, timeout]);
      if (timer) clearTimeout(timer);
    }
    if (!map || map.size === 0) return { bulk: selected, crossDock: [] as T[] };

    const bulk: T[] = [];
    const crossDock: T[] = [];
    selected.forEach(line => {
      const candidate = line.itemNo ? map!.get(keyOf(warehouseOf(line), line.itemNo)) : undefined;
      // Standard-lager har bare informasjon, ikke crossdock-utlegg — der går linjen i masse-mottaket.
      (candidate?.isAdvancedWarehouse ? crossDock : bulk).push(line);
    });
    return { bulk, crossDock };
  }, [warehouseOf]);

  return { candidateFor, splitForBulk };
}
